import { useAuth } from '@clerk/nextjs';
import { useQuery } from '@tanstack/react-query';
import { Query } from '@web/constants/query';
import { hasSubscription } from '@web/guards/Subscription';
import { App as AppQuery } from '@web/query/App';
import axios from 'axios';
import React from 'react';
import { useProductsForUpdate } from './useProductsForUpdate';

export interface BodyPayload {
  customerId: string;
  subscriptionId: string;
  subscriptionItemId: string;
  priceId: string;
  qty: number;
  prorationDate: number;
}

export interface Failure {
  message: string;
}

export interface DataResponse {
  amountDue: number;
  amountPaid: number;
  amountRemaining: number;
  tax: number;
  total: number;
  subTotal: number;
}

export interface ApiResponse {
  success: true;
  data: DataResponse;
}

export interface Params {
  priceId: string;
  qty: number;
  disable?: boolean;
}

/**
 * React Query hook to fetch preview prorata amount based on selected new plan and users count
 * @returns ApiResponse | undefined
 */
const usePreviewProrata = ({ priceId, qty, disable = false }: Params) => {
  const { isSignedIn } = useAuth();

  const { data: subscriptionData } = AppQuery.useSubscriptions();
  const { isLoading } = useProductsForUpdate();

  const previewPayload = React.useMemo(() => {
    if (hasSubscription(subscriptionData)) {
      let prorationDate = Math.floor(Date.now() / 1000);

      const payload = {
        customerId: subscriptionData?.subscription?.customer,
        subscriptionId: subscriptionData?.subscription?.id,
        subscriptionItemId: subscriptionData?.subscription?.items?.data[0]?.id,
        priceId,
        qty,
        prorationDate,
        isTrialMode: subscriptionData?.subscription?.status === 'trialing',
      };

      return payload;
    }
  }, [subscriptionData, priceId, qty]);

  return useQuery<DataResponse, Failure>({
    queryKey: [Query.StripeKeys.previewProrata, { ...previewPayload }],
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !disable && !!isSignedIn && !isLoading && !!previewPayload,
    staleTime: Infinity,
    queryFn: async () => {
      // query enabled only when previewPayload is available
      const { subscriptionId, ...payload } = previewPayload!;
      const response = await axios.post<ApiResponse>(
        `${process.env.NEXT_PUBLIC_API_APP_ENDPOINT}/subscription/${subscriptionId}/preview-prorata`,
        payload,
      );
      return response.data.data;
    },
  });
};

export { usePreviewProrata };
